import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdAccountBalance } from 'react-icons/md';
import './MasterData.css';

interface BankAccount {
  bankAccountId: number;
  accountCode: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currency: string;
  currentBalance: number;
  isActive: string;
}

const BankAccounts: React.FC = () => {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);

  useEffect(() => {
    // Mock data
    setBankAccounts([
      {
        bankAccountId: 1,
        accountCode: 'BA001',
        bankName: 'Banco de Oro (BDO)',
        branchName: 'Makati Business District',
        accountNumber: '0012-3456-7890',
        accountName: 'DEWFOAM Furniture Corporation',
        accountType: 'CHECKING',
        currency: 'PHP',
        currentBalance: 3500000.00,
        isActive: 'Y'
      },
      {
        bankAccountId: 2,
        accountCode: 'BA002',
        bankName: 'Bank of the Philippine Islands (BPI)',
        branchName: 'Ortigas Center',
        accountNumber: '0023-4567-8901',
        accountName: 'DEWFOAM Furniture Corporation',
        accountType: 'SAVINGS',
        currency: 'PHP',
        currentBalance: 1200000.00,
        isActive: 'Y'
      },
      {
        bankAccountId: 3,
        accountCode: 'BA003',
        bankName: 'Metropolitan Bank (Metrobank)',
        branchName: 'BGC Branch',
        accountNumber: '0034-5678-9012',
        accountName: 'DEWFOAM Furniture Corporation',
        accountType: 'CHECKING',
        currency: 'USD',
        currentBalance: 25000.00,
        isActive: 'Y'
      },
      {
        bankAccountId: 4,
        accountCode: 'BA004',
        bankName: 'Banco de Oro (BDO)',
        branchName: 'Manila Branch',
        accountNumber: '0045-6789-0123',
        accountName: 'DEWFOAM Furniture Payroll Account',
        accountType: 'SAVINGS',
        currency: 'PHP',
        currentBalance: 850000.00,
        isActive: 'Y'
      }
    ]);
  }, []);

  const filteredAccounts = bankAccounts.filter(account =>
    account.bankName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.accountCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.accountNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getTotalBalance = () => {
    const phpTotal = bankAccounts
      .filter(acc => acc.currency === 'PHP' && acc.isActive === 'Y')
      .reduce((sum, acc) => sum + acc.currentBalance, 0);
    const usdTotal = bankAccounts
      .filter(acc => acc.currency === 'USD' && acc.isActive === 'Y')
      .reduce((sum, acc) => sum + acc.currentBalance, 0);

    return { php: phpTotal, usd: usdTotal };
  };

  const totals = getTotalBalance();

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Bank Accounts</h2>
            <p className="subtitle">Manage company bank accounts</p>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditingAccount(null); setShowModal(true); }}>
            <MdAdd /> Add New Account
          </button>
        </div>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">
              <MdAccountBalance />
            </div>
            <div className="summary-content">
              <p className="summary-label">Total PHP Balance</p>
              <h3 className="summary-value">{formatCurrency(totals.php, 'PHP')}</h3>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">
              <MdAccountBalance />
            </div>
            <div className="summary-content">
              <p className="summary-label">Total USD Balance</p>
              <h3 className="summary-value">{formatCurrency(totals.usd, 'USD')}</h3>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search bank accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Bank Name</th>
                  <th>Branch</th>
                  <th>Account Number</th>
                  <th>Account Name</th>
                  <th>Type</th>
                  <th>Currency</th>
                  <th>Current Balance</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map(account => (
                  <tr key={account.bankAccountId}>
                    <td className="code">{account.accountCode}</td>
                    <td className="name">{account.bankName}</td>
                    <td>{account.branchName}</td>
                    <td><code>{account.accountNumber}</code></td>
                    <td>{account.accountName}</td>
                    <td>
                      <span className={`badge ${account.accountType === 'CHECKING' ? 'badge-blue' : 'badge-green'}`}>
                        {account.accountType}
                      </span>
                    </td>
                    <td><strong>{account.currency}</strong></td>
                    <td className="amount">{formatCurrency(account.currentBalance, account.currency)}</td>
                    <td>
                      <span className={`badge ${account.isActive === 'Y' ? 'badge-success' : 'badge-danger'}`}>
                        {account.isActive === 'Y' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon btn-edit" onClick={() => { setEditingAccount(account); setShowModal(true); }}>
                          <MdEdit />
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => {
                          if (window.confirm('Delete this bank account?')) {
                            setBankAccounts(bankAccounts.filter(a => a.bankAccountId !== account.bankAccountId));
                          }
                        }}>
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <BankAccountModal
          account={editingAccount}
          onClose={() => setShowModal(false)}
          onSave={(account) => {
            if (editingAccount) {
              setBankAccounts(bankAccounts.map(a => a.bankAccountId === account.bankAccountId ? account : a));
            } else {
              setBankAccounts([...bankAccounts, { ...account, bankAccountId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface BankAccountModalProps {
  account: BankAccount | null;
  onClose: () => void;
  onSave: (account: BankAccount) => void;
}

const BankAccountModal: React.FC<BankAccountModalProps> = ({ account, onClose, onSave }) => {
  const [formData, setFormData] = useState<BankAccount>(
    account || {
      bankAccountId: 0,
      accountCode: '',
      bankName: '',
      branchName: '',
      accountNumber: '',
      accountName: '',
      accountType: 'CHECKING',
      currency: 'PHP',
      currentBalance: 0,
      isActive: 'Y'
    }
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{account ? 'Edit Bank Account' : 'Add New Bank Account'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Account Code *</label>
                <input type="text" required value={formData.accountCode}
                  onChange={(e) => setFormData({ ...formData, accountCode: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Bank Name *</label>
                <select value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}>
                  <option value="">Select Bank</option>
                  <option value="Banco de Oro (BDO)">Banco de Oro (BDO)</option>
                  <option value="Bank of the Philippine Islands (BPI)">Bank of the Philippine Islands (BPI)</option>
                  <option value="Metropolitan Bank (Metrobank)">Metropolitan Bank (Metrobank)</option>
                  <option value="Land Bank of the Philippines">Land Bank of the Philippines</option>
                  <option value="Philippine National Bank (PNB)">Philippine National Bank (PNB)</option>
                  <option value="Security Bank">Security Bank</option>
                  <option value="UnionBank">UnionBank</option>
                  <option value="Chinabank">Chinabank</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Branch Name *</label>
                <input type="text" required value={formData.branchName}
                  onChange={(e) => setFormData({ ...formData, branchName: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Account Number *</label>
                <input type="text" required value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Account Name *</label>
                <input type="text" required value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Account Type *</label>
                <select value={formData.accountType}
                  onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}>
                  <option value="CHECKING">Checking</option>
                  <option value="SAVINGS">Savings</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Currency *</label>
                <select value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}>
                  <option value="PHP">PHP - Philippine Peso</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>
              <div className="form-group">
                <label>Current Balance</label>
                <input type="number" step="0.01" value={formData.currentBalance}
                  onChange={(e) => setFormData({ ...formData, currentBalance: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <select value={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}>
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{account ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankAccounts;
